/**
 * Formats a price in cents to a USD string, e.g. 1234 -> '$12.34'.
 * Returns 'N/A' if input is null or undefined.
 */
import * as Sentry from '@sentry/react';
export const formatPrice = (priceInCents) => {
  if (priceInCents === undefined || priceInCents === null) return 'N/A';
  return `$${(priceInCents / 100).toFixed(2)}`;
};

/**
 * Parses a price input (number or string with currency symbols) to a numeric value.
 * Returns 0 if parsing fails.
 */
export const parsePrice = (price) => {
  if (price === undefined || price === null) return 0;
  if (typeof price === 'number') return price;
  const numeric = parseFloat(price.replace(/[^0-9.]/g, ''));
  return isNaN(numeric) ? 0 : numeric;
};

/**
 * Determines if a price value is likely in cents rather than dollars.
 * Uses heuristics based on the value and format to make this determination.
 *
 * @param {number|string} price - The price value to check
 * @returns {boolean} - True if the price is likely in cents, false otherwise
 */
export const isPriceInCents = (price) => {
  if (price === undefined || price === null) {
    return false;
  }

  // If it's a string without a decimal point, it's likely in cents
  if (typeof price === 'string' && !price.includes('.')) {
    const numericValue = parsePrice(price);
    const result = numericValue > 20;
    return result;
  }

  // If it's a number without decimal places and reasonably large
  if (typeof price === 'number' && Number.isInteger(price) && price > 20) {
    return true;
  }

  return false;
};

/**
 * Normalizes a price to cents regardless of its original format.
 * If the price is already in cents, it returns the value unchanged.
 * If the price is in dollars, it converts to cents.
 *
 * @param {number|string} price - The price value to normalize
 * @returns {number} - The price in cents
 */
export const normalizeToCents = (price) => {
  // If it's undefined or null, return 0
  if (price === undefined || price === null) {
    return 0;
  }

  const numericValue = parsePrice(price);

  // Already in cents - don't convert
  if (isPriceInCents(price)) {
    return numericValue;
  }

  // If value comes from cart calculations (subtotal, tax), it's already in dollars
  // We need to convert to cents for display formatting
  return Math.round(numericValue * 100);
};

/**
 * Formats a price value to a display string, handling both cents and dollars format.
 *
 * @param {number|string} price - The price value to format
 * @returns {string} - The formatted price string (e.g. "$2.50")
 */
export const formatDisplayPrice = (price) => {
  if (price === undefined || price === null) return 'N/A';

  const priceInCents = normalizeToCents(price);

  const result = formatPrice(priceInCents);

  return result;
};

/**
 * Calculates the total price of modifiers
 * @param {Array|Object} modifiers - The modifiers array or object
 * @returns {number} - Total modifier price
 */
export const calculateModifiersTotal = (modifiers) => {
  if (!modifiers) return 0;

  // Handle array format (simplified modifiers)
  if (Array.isArray(modifiers)) {
    return modifiers.reduce((sum, mod) => sum + (mod.price || 0), 0);
  }

  // Handle object format (legacy modifiers) - would need originalItem context
  // For now, return 0 as this requires more complex logic with originalItem
  return 0;
};

/**
 * Calculates the base price for a cart item, handling various data structures
 * and price calculation scenarios.
 *
 * @param {Object} item - The cart item object
 * @returns {number} - The calculated base price
 */
export const calculateBasePrice = (item) => {
  if (!item) return 0;

  try {
    // Priority 1: Use explicit basePrice if available
    if (item.basePrice !== undefined && item.basePrice !== null) {
      return item.basePrice;
    }

    // Priority 2: Use originalItem basePrice
    if (
      item.originalItem?.basePrice !== undefined &&
      item.originalItem.basePrice !== null
    ) {
      return item.originalItem.basePrice;
    }

    // Priority 3: Calculate from total price minus modifiers
    if (item.price !== undefined && item.price !== null && item.modifiers) {
      const modifiersTotal = calculateModifiersTotal(item.modifiers);
      if (modifiersTotal > 0) {
        return item.price - modifiersTotal;
      }
    }

    // Priority 4: Fallback to various price sources
    return item.price || item.originalItem?.price || item.baseItem?.price || 0;
  } catch (error) {
    // If calculation fails, return 0 rather than throwing
    Sentry.captureException(error, {
      tags: {
        component: 'price',
        action: 'calculateBasePrice',
      },
      extra: { item },
    });
    return 0;
  }
};

/**
 * Calculates the subtotal for a cart item
 * @param {Object} item - The cart item object
 * @returns {number} - The calculated subtotal
 */
export const calculateItemSubtotal = (item) => {
  if (!item) return 0;

  try {
    // Use explicit subtotal if available
    if (item.subtotal !== undefined && item.subtotal !== null) {
      return item.subtotal;
    }

    // Calculate from price * quantity
    const itemPrice = item.price || item.baseItem?.price || 0;
    const quantity = item.quantity || 1;

    return itemPrice * quantity;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'price',
        action: 'calculateItemSubtotal',
      },
      extra: { item },
    });
    return 0;
  }
};
